/**
 * @fileoverview Text component for rendering localized text with TypographyStyle
 * @module screen-view-v2/components/shared/Text
 *
 * V2 adaptation: LocalizedLabel replaces Text type.
 * - resolveLabel() instead of resolveText()
 * - mapTypographyStyles() instead of mapLabelBlockStyles()
 * - mapFrameLayoutStyles() instead of mapBoxBlockStyles()
 * - label.templateParams instead of text.parameters
 */

import React, { memo, useContext, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { LocalizedLabel } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import { resolveRenderedLabel } from '../../../utils/renderPredicates';
import { mapTypographyStyles, mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { parseTemplateTags, type ParsedSegment } from '../../../utils/templateTagParser';
import { useFontOverrides } from '@modules/font-config/context/FontConfigContext';
import type { SemanticTextTag } from '@modules/screen-view-v2/utils/semanticMarkup';
import { InlineCountdown } from '../InlineCountdown';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { ArticleRenderContext } from '@modules/screen-view-v2/context/ArticleRenderContext';
import { articleLinkTargetProps, resolveArticleHref } from '@modules/screen-view-v2/utils/articleLinks';
import { resolveLocaleText } from '@modules/screen-view-v2/utils/articleText';
import { useElementAnimation } from '../../../animations';
import styles from './Text.module.css';

export interface TextProps {
  /** LocalizedLabel from v2 schema */
  label: LocalizedLabel | undefined;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  style?: CSSProperties;
  /**
   * Semantic element for the text body (articles only — see
   * SemanticMarkupContext). Absent = the default `div`, byte-identical to the
   * pre-semantic markup.
   */
  as?: SemanticTextTag;
  /**
   * Article template (G2, D4): text-role style (custom-property references), applied UNDER the
   * label's literal typography — a literal fontSize/color/… on the label still wins. Absent outside
   * articles, so funnel output is unchanged.
   */
  roleStyle?: CSSProperties;
  /** Element id of the text body (article heading slug, G2 D5). Absent outside articles. */
  id?: string;
}

export const Text = memo(function Text({
  label,
  locale,
  variables,
  resolvedScreenValues,
  className,
  'data-testid': testId,
  editPath,
  style,
  as,
  roleStyle,
  id,
}: TextProps): React.ReactElement | null {
  const fontOverrides = useFontOverrides();
  // Article template (G2, D8): null outside articles → links render exactly as before.
  const article = useContext(ArticleRenderContext);
  // Self-applied element animation (plan lookup by own editPath, e.g.
  // content.copy.title) — Text is the universal mount point for text refs,
  // including screens that render Text directly without CopyBlock (HeroContent)
  const textAnim = useElementAnimation(editPath);
  // null = Text renders nothing (shared with the entrance plan: utils/renderPredicates.ts)
  const content = resolveRenderedLabel(label, locale, variables);

  // Serialize templateParams for deep comparison in useMemo
  const templateParamsKey = label ? JSON.stringify(label.templateParams) : '';

  const parseResult = useMemo(() => {
    if (!label || !content) return { hasLabels: false, hasLinks: false, hasCountdowns: false, segments: [] as ParsedSegment[] };
    const labels = label.templateParams?.labels || {};
    const links = label.templateParams?.links || {};
    const countdowns = label.templateParams?.countdowns || {};
    return parseTemplateTags(content, labels, links, resolvedScreenValues || {}, countdowns);
  }, [label, content, templateParamsKey, resolvedScreenValues]);

  if (!label || content === null) {
    return null;
  }

  // V2: mapTypographyStyles replaces mapLabelBlockStyles
  const allTypographyStyles = mapTypographyStyles(label.appearance, fontOverrides);

  // Split: background props go to inline <span>, rest stays on div
  const { backgroundColor, borderRadius, ...textStyles } = allTypographyStyles;

  const bgSpanStyles: CSSProperties | undefined = backgroundColor
    ? {
        backgroundColor,
        ...(borderRadius !== undefined && { borderRadius }),
        padding: '0 4px',
        boxDecorationBreak: 'clone',
        WebkitBoxDecorationBreak: 'clone',
      } as CSSProperties
    : undefined;

  // V2: mapFrameLayoutStyles replaces mapBoxBlockStyles
  const layoutStyles: CSSProperties = mapFrameLayoutStyles(label.layout);

  const renderSegments = (segments: ParsedSegment[]) => {
    return segments.map((segment, index) => {
      if (segment.type === 'text') {
        return <span key={index}>{segment.content}</span>;
      }

      if (segment.type === 'label') {
        // V2 templateTagParser already uses TypographyStyle
        const segmentStyles = mapTypographyStyles(segment.styles, fontOverrides);
        return (
          <span key={index} style={segmentStyles}>
            {segment.content}
          </span>
        );
      }

      if (segment.type === 'countdown') {
        const countdownEl = (
          <InlineCountdown
            key={segment.labelStyles ? undefined : index}
            config={segment.config}
            data-testid={`inline-countdown-${segment.timerName}`}
          />
        );
        if (segment.labelStyles) {
          const labelSx = mapTypographyStyles(segment.labelStyles, fontOverrides);
          return <span key={index} style={labelSx}>{countdownEl}</span>;
        }
        return countdownEl;
      }

      if (segment.type === 'link') {
        const labelSx = segment.labelStyles ? mapTypographyStyles(segment.labelStyles, fontOverrides) : undefined;

        if (article) {
          const href = resolveArticleHref(segment.isExternal ? segment.href : segment.value);
          if (href) {
            const options = label?.templateParams?.linkOptions?.[segment.linkName];
            const text = resolveLocaleText(options?.label, locale) || segment.linkName;
            const articleLink = (
              <a
                key={labelSx ? undefined : index}
                href={href}
                className={segment.isExternal ? styles.externalLink : undefined}
                {...articleLinkTargetProps(href, options?.target, article.runtime?.siteOrigin)}
                data-article-link=""
                style={labelSx}
              >
                {text}
              </a>
            );
            return labelSx ? <span key={index}>{articleLink}</span> : articleLink;
          }
        }

        if (segment.isExternal) {
          const linkEl = (
            <a
              key={labelSx ? undefined : index}
              href={segment.href}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.externalLink}
              style={labelSx}
            >
              {segment.linkName}
            </a>
          );
          return labelSx ? <span key={index}>{linkEl}</span> : linkEl;
        }

        const displayValue = segment.href || `@${segment.linkName}`;
        return (
          <span key={index} className={styles.screenLink} style={labelSx}>
            {displayValue}
          </span>
        );
      }

      return null;
    });
  };

  const hasTemplateTags = parseResult.hasLabels || parseResult.hasLinks || parseResult.hasCountdowns;
  const schemaTestId = testId ?? getSchemaTestId(editPath);
  const baseClassName = className ? `${styles.text} ${className}` : styles.text;
  const ContentTag = as ?? 'div';
  const contentClassName = as ? `${baseClassName} ${styles.semantic}` : baseClassName;

  return (
    <div
      className={textAnim.className}
      style={{ width: '100%', ...layoutStyles, ...style, ...textAnim.style }}
      data-anim={textAnim['data-anim']}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      <ContentTag
        id={id}
        className={contentClassName}
        style={{ width: '100%', ...roleStyle, ...textStyles }}
        data-testid={schemaTestId ? `${schemaTestId}.content` : undefined}
      >
        {bgSpanStyles ? (
          <span style={bgSpanStyles}>
            {hasTemplateTags ? renderSegments(parseResult.segments) : content}
          </span>
        ) : (
          hasTemplateTags ? renderSegments(parseResult.segments) : content
        )}
      </ContentTag>
    </div>
  );
});

Text.displayName = 'Text';
